export const CHAT_INIT_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000] as const;
export const CHAT_INIT_STEADY_RETRY_MS = 60_000;

/** After this many failures that happened after a backend login, stop auto-retrying: each one may have minted a Matrix device. */
export const CHAT_INIT_MAX_POST_LOGIN_FAILURES = 5;
export const CHAT_INIT_MAX_SOFT_LOGOUTS = 5;
export const CHAT_INIT_SOFT_LOGOUT_WINDOW_MS = 5 * 60_000;
export const CHAT_INIT_AUTO_RETRY_MIN_INTERVAL_MS = 30_000;
const JITTER_RATIO = 0.25;

/**
 * Delay before retry number `failureCount` (1 = after the first failure),
 * spread by +-25% so many tabs/users hit by one outage do not retry in lockstep.
 */
export function getChatInitRetryDelayMs(failureCount: number, random: () => number = Math.random): number {
    const base = CHAT_INIT_BACKOFF_MS[failureCount - 1] ?? CHAT_INIT_STEADY_RETRY_MS;
    return Math.round(base * (1 - JITTER_RATIO + 2 * JITTER_RATIO * random()));
}

const PERMANENT_MATRIX_ERRCODES = new Set([
    'M_UNKNOWN_TOKEN',
    'M_MISSING_TOKEN',
    'M_FORBIDDEN',
    'M_USER_DEACTIVATED',
]);

/**
 * True when retrying cannot help without user action (revoked or rejected
 * auth, chat not permitted). Covers the two shapes init errors take: RTK Query
 * errors from the backend login ({status}) and matrix-js-sdk HTTP errors
 * ({httpStatus, errcode}). Transient statuses (408, 425, 429, 5xx, network)
 * stay retryable.
 */
export function isPermanentChatInitError(error: unknown): boolean {
    const e = error as {status?: unknown; httpStatus?: unknown; errcode?: unknown; data?: {errcode?: unknown}} | null;
    if (!e || typeof e !== 'object') return false;

    const status = typeof e.status === 'number' ? e.status : typeof e.httpStatus === 'number' ? e.httpStatus : null;
    if (status !== null && status >= 400 && status < 500 && ![408, 425, 429].includes(status)) {
        return true;
    }
    const errcode = e.errcode ?? e.data?.errcode;
    return typeof errcode === 'string' && PERMANENT_MATRIX_ERRCODES.has(errcode);
}

/** 'blocked' means the run returned at an early guard without attempting anything. */
export type ChatInitRunResult = 'blocked' | void;

export interface ChatInitRetryScheduler {
    /** Count a failure and schedule the next attempt; returns the delay used. */
    scheduleAfterFailure(minFailureCount?: number): number;
    /** Run the attempt now, replacing any pending timer. */
    retryNow(): void;
    /** Drop the pending timer and the failure count (logout, account change, success). */
    cancel(): void;
    readonly failureCount: number;
    readonly pending: boolean;
}

export function createChatInitRetryScheduler(
    run: () => ChatInitRunResult | Promise<ChatInitRunResult>,
    random: () => number = Math.random,
): ChatInitRetryScheduler {
    let failureCount = 0;
    let epoch = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // A run that hit an early guard must not end the chain; only cancel() (logout,
    // account change, lock loss, success) does. Re-arm at the same backoff step.
    const fire = () => {
        const firedEpoch = epoch;
        void Promise.resolve(run()).then(result => {
            if (result === 'blocked' && epoch === firedEpoch && timer === null && failureCount > 0) {
                arm();
            }
        });
    };

    const arm = (): number => {
        const delay = getChatInitRetryDelayMs(failureCount, random);
        timer = setTimeout(() => {
            timer = null;
            fire();
        }, delay);
        return delay;
    };

    const clearTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    return {
        scheduleAfterFailure(minFailureCount = 0) {
            clearTimer();
            failureCount = Math.max(failureCount + 1, minFailureCount);
            return arm();
        },
        retryNow() {
            clearTimer();
            fire();
        },
        cancel() {
            clearTimer();
            epoch += 1;
            failureCount = 0;
        },
        get failureCount() {
            return failureCount;
        },
        get pending() {
            return timer !== null;
        },
    };
}

/**
 * Remembers across retries which Matrix device a failed attempt logged in as,
 * so the next login reuses it (a null deviceId makes the backend mint a new
 * device each time), and counts failures that happened after a login.
 */
export class ChatInitAttemptTracker {
    private retryDevice: {tenant: string; userId: string; deviceId: string} | null = null;
    private postLoginFailures = 0;
    private softLogouts = 0;
    private lastSoftLogoutAtMs: number | null = null;

    /** A saved device is only offered to the account it was created for. */
    nextLoginDeviceId(tenant: string, userId: string, reduxDeviceId: string | null, storedDeviceId: string | null): string | null {
        const saved = this.retryDevice;
        const own = saved && saved.tenant === tenant && saved.userId === userId ? saved.deviceId : null;
        return reduxDeviceId ?? own ?? storedDeviceId;
    }

    setDevice(tenant: string, userId: string, deviceId: string | null): void {
        this.retryDevice = deviceId ? {tenant, userId, deviceId} : null;
    }

    recordFailure(opts: {tenant: string; userId: string; loggedIn: boolean; createdDeviceId: string | null}): void {
        if (opts.createdDeviceId) this.setDevice(opts.tenant, opts.userId, opts.createdDeviceId);
        if (opts.loggedIn) this.postLoginFailures += 1;
    }

    recordSoftLogout(nowMs: number): number {
        if (this.lastSoftLogoutAtMs !== null && nowMs - this.lastSoftLogoutAtMs > CHAT_INIT_SOFT_LOGOUT_WINDOW_MS) {
            this.softLogouts = 0;
        }
        this.lastSoftLogoutAtMs = nowMs;
        this.softLogouts += 1;
        return this.softLogouts;
    }

    resetSoftLogouts(): void {
        this.softLogouts = 0;
        this.lastSoftLogoutAtMs = null;
    }

    get softLogoutCount(): number {
        return this.softLogouts;
    }

    get softLogoutsExhausted(): boolean {
        return this.softLogouts >= CHAT_INIT_MAX_SOFT_LOGOUTS;
    }

    get failuresAfterLogin(): number {
        return this.postLoginFailures;
    }

    get exhausted(): boolean {
        return this.postLoginFailures >= CHAT_INIT_MAX_POST_LOGIN_FAILURES;
    }

    reset(): void {
        this.retryDevice = null;
        this.postLoginFailures = 0;
    }
}

export type ChatInitFailureReason = 'classified' | 'retries_exhausted' | 'soft_logout_loop';

export interface ChatInitFailureState {
    permanent: boolean;
    reason?: ChatInitFailureReason;
}

export function isAutoRetryableChatInitFailure(failure: ChatInitFailureState | null): boolean {
    return failure !== null && failure.permanent && failure.reason === 'retries_exhausted';
}

export interface ChatInitAutoRetryOptions {
    canRetry: () => boolean;
    retry: () => void;
    now?: () => number;
    minIntervalMs?: number;
    win?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
    doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
}

export function attachChatInitAutoRetry(options: ChatInitAutoRetryOptions): () => void {
    const now = options.now ?? Date.now;
    const minIntervalMs = options.minIntervalMs ?? CHAT_INIT_AUTO_RETRY_MIN_INTERVAL_MS;
    const win = options.win ?? window;
    const doc = options.doc ?? document;
    let lastAttemptAtMs: number | null = null;

    const trigger = () => {
        if (!options.canRetry()) return;
        const nowMs = now();
        if (lastAttemptAtMs !== null && nowMs - lastAttemptAtMs < minIntervalMs) return;
        lastAttemptAtMs = nowMs;
        options.retry();
    };
    const onVisibilityChange = () => {
        if (doc.visibilityState === 'visible') trigger();
    };

    win.addEventListener('online', trigger);
    doc.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
        win.removeEventListener('online', trigger);
        doc.removeEventListener('visibilitychange', onVisibilityChange);
    };
}

export type SoftLogoutGate = 'idle' | 'pending' | 'capped';

export interface SoftLogoutRecoveryDeps {
    tracker: ChatInitAttemptTracker;
    schedule: (minFailureCount: number) => number;
    cancel: () => void;
    setFailure: (failure: ChatInitFailureState) => void;
    releaseLock: () => void;
    bumpRevision: () => void;
    resetInitState: () => void;
    now?: () => number;
}

export interface SoftLogoutExpiryOutcome {
    softLogouts: number;
    exhausted: boolean;
    retryDelayMs: number | null;
}

export interface SoftLogoutRecovery {
    onAuthenticationExpired(): SoftLogoutExpiryOutcome;
    isGateOpen(): boolean;
    beforeScheduledRun(): void;
    onManualRetry(): void;
    reset(): void;
    readonly gate: SoftLogoutGate;
}

export function createSoftLogoutRecovery(deps: SoftLogoutRecoveryDeps): SoftLogoutRecovery {
    const now = deps.now ?? Date.now;
    let gate: SoftLogoutGate = 'idle';

    return {
        onAuthenticationExpired() {
            deps.bumpRevision();
            deps.resetInitState();
            deps.releaseLock();
            const softLogouts = deps.tracker.recordSoftLogout(now());
            const exhausted = deps.tracker.softLogoutsExhausted;
            if (exhausted) {
                gate = 'capped';
                deps.cancel();
                deps.setFailure({permanent: true, reason: 'soft_logout_loop'});
                return {softLogouts, exhausted, retryDelayMs: null};
            }
            gate = 'pending';
            return {softLogouts, exhausted, retryDelayMs: deps.schedule(softLogouts)};
        },
        isGateOpen() {
            return gate === 'idle';
        },
        beforeScheduledRun() {
            if (gate === 'pending') gate = 'idle';
        },
        onManualRetry() {
            gate = 'idle';
            deps.tracker.resetSoftLogouts();
        },
        reset() {
            gate = 'idle';
        },
        get gate() {
            return gate;
        },
    };
}
